import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const SECTIONS = [
  {
    title: '1. Responsable del Tratamiento',
    body: `El servidor Frostbound es el responsable del tratamiento de los datos personales recogidos a través de este portal web. Puedes contactarnos en staff@frostbound.wow para cualquier consulta relacionada con tu privacidad.`,
  },
  {
    title: '2. Datos que Recopilamos',
    body: `Al registrarte en Frostbound recopilamos los siguientes datos:

• Nombre de usuario: necesario para crear tu cuenta de juego.
• Dirección de correo electrónico: utilizada únicamente para la recuperación de contraseña y comunicaciones importantes del servidor.
• Contraseña: almacenada de forma segura mediante hash (bcrypt para el portal, SRP6 para el cliente de juego). Nunca guardamos tu contraseña en texto plano.
• Dirección IP: registrada en el momento del registro y los accesos, con fines de seguridad y detección de fraude.
• Fecha de registro y último acceso: para gestión interna de cuentas.

No recopilamos datos bancarios ni información de pago directamente — las transacciones de donación se procesan a través de pasarelas de pago externas con sus propias políticas de privacidad.`,
  },
  {
    title: '3. Finalidad del Tratamiento',
    body: `Utilizamos tus datos para:

• Crear y gestionar tu cuenta de juego en el servidor.
• Permitirte acceder al portal web de Frostbound.
• Enviarte notificaciones importantes sobre el servidor (mantenimientos, wipes, cambios en los términos).
• Detectar y prevenir comportamientos fraudulentos o abusivos.
• Mejorar la experiencia del servidor basándonos en estadísticas de uso anonimizadas.

No utilizamos tus datos para publicidad ni los cedemos a terceros con fines comerciales.`,
  },
  {
    title: '4. Base Legal del Tratamiento',
    body: `El tratamiento de tus datos se basa en:

• La ejecución del contrato de servicio que aceptas al crear tu cuenta (Términos de Servicio).
• Tu consentimiento explícito, otorgado al marcar la casilla de aceptación en el formulario de registro.
• El interés legítimo de Frostbound para garantizar la seguridad y el correcto funcionamiento del servidor.`,
  },
  {
    title: '5. Conservación de Datos',
    body: `Conservamos tus datos mientras tu cuenta esté activa. Si solicitas la eliminación de tu cuenta, procederemos a borrar tus datos personales en un plazo máximo de 30 días, salvo que exista obligación legal de conservarlos.

Los logs de seguridad (IPs, accesos) se conservan durante un máximo de 90 días.`,
  },
  {
    title: '6. Seguridad',
    body: `Aplicamos medidas técnicas y organizativas para proteger tus datos:

• Contraseñas hasheadas con bcrypt (portal) y SRP6 (cliente de juego).
• Sesiones gestionadas mediante cookies httpOnly, no accesibles desde JavaScript.
• Acceso a la base de datos restringido a los administradores del servidor.
• Comunicaciones con el portal protegidas mediante HTTPS en el entorno de producción.

A pesar de estas medidas, ningún sistema es 100% seguro. Te recomendamos usar una contraseña única para Frostbound.`,
  },
  {
    title: '7. Tus Derechos',
    body: `Tienes derecho a:

• Acceder a los datos que tenemos sobre ti.
• Rectificar datos incorrectos o incompletos.
• Solicitar la eliminación de tu cuenta y datos personales.
• Oponerte al tratamiento de tus datos.
• Portabilidad de datos (en la medida técnicamente posible).

Para ejercer cualquiera de estos derechos, escríbenos a staff@frostbound.wow indicando tu nombre de usuario y la solicitud concreta.`,
  },
  {
    title: '8. Cookies',
    body: `Frostbound utiliza exclusivamente cookies técnicas necesarias para el funcionamiento del portal:

• Cookie de sesión (access_token): cookie httpOnly que mantiene tu sesión iniciada durante 24 horas. Se elimina al cerrar sesión.

No utilizamos cookies de seguimiento, analíticas de terceros ni publicidad.
Contenido incrustado: las guías en vídeo de Primeros pasos se reproducen desde TikTok o YouTube. El reproductor no se carga hasta que tú lo pides: antes te avisamos y, solo si aceptas, tu navegador se conecta con esa plataforma, que puede recibir tu dirección IP e instalar sus propias cookies según su política de privacidad. Los vídeos de YouTube se cargan en su modo de privacidad mejorada (youtube-nocookie.com). Si marcas «Recordar mi elección», esa preferencia se guarda solo en tu navegador (almacenamiento local) y no se envía a nuestros servidores.`,
  },
  {
    title: '9. Menores de Edad',
    body: `Frostbound no recopila intencionadamente datos de menores de 13 años. Si eres padre o tutor y crees que tu hijo ha creado una cuenta sin tu consentimiento, contacta con nosotros en staff@frostbound.wow y procederemos a eliminar la cuenta.`,
  },
  {
    title: '10. Cambios en esta Política',
    body: `Podemos actualizar esta Política de Privacidad ocasionalmente. Los cambios serán publicados en esta página con la fecha de última actualización. El uso continuado del servicio tras la publicación implica la aceptación de la nueva política.

Última actualización: octubre de 2026`,
  },
];

export default function PrivacidadPage() {
  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Política de privacidad"
        description="Cómo Frostbound gestiona tus datos personales, cookies y seguridad de la cuenta según la normativa vigente."
        canonical="/privacidad"
      />
      <Navbar />
      <main className="legal-main">
        <div className="legal-header anim-fade-up">
          <h1 className="shop-title">
            <ShieldCheck size={28} className="shop-title-icon" />
            POLÍTICA DE PRIVACIDAD
          </h1>
          <p className="shop-subtitle">
            Cómo protegemos y usamos tus datos en{' '}
            <span className="cyan-highlight">Frostbound.</span>
          </p>
        </div>

        <div className="legal-body anim-fade-up" style={{ animationDelay: '0.2s' }}>
          {SECTIONS.map((s) => (
            <div key={s.title} className="legal-section">
              <h2 className="legal-section-title">{s.title}</h2>
              <p className="legal-section-body">{s.body}</p>
            </div>
          ))}

          <div className="legal-back">
            <Link to="/registro" className="home-info-link">
              ← Volver al registro
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </PageWrapper>
  );
}
